import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * AutoFitText
 * - Forces single-line (no wrap)
 * - No ellipsis (clip)
 * - Avoids cutting by shrinking font-size until text fits width
 *
 * Notes:
 * - If the container is too narrow even at minFontSize, text may still clip.
 * - Uses ResizeObserver + MutationObserver to refit when layout or translated text changes.
 */
const AutoFitText = ({
  children,
  minFontSize = 10,
  maxFontSize,
  className,
  style,
  title,
  ...rest
}) => {
  const elRef = useRef(null);
  // null means "use the normal CSS font-size" (keeps English/original design)
  const [fontSizePx, setFontSizePx] = useState(null);

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return;

    let rafId = 0;
    let ro = null;
    let mo = null;

    const fit = () => {
      if (!el) return;

      // Determine starting font size (max)
      const computed = window.getComputedStyle(el);
      const computedPx = parseFloat(computed.fontSize || '0') || 0;
      const start = Math.max(
        0,
        typeof maxFontSize === 'number' && maxFontSize > 0 ? maxFontSize : computedPx
      );

      // Reset to default size first (so we preserve original English sizing when it fits)
      el.style.fontSize = '';

      // If it already fits at default size, keep it (no shrink).
      if (el.clientWidth > 0 && el.scrollWidth <= el.clientWidth) {
        setFontSizePx(null);
        return;
      }

      // If it overflows, start from current computed size (or provided maxFontSize) and shrink.
      let size = start;
      if (size > 0) el.style.fontSize = `${size}px`;

      // Guard against weird zero-width measurements during mount.
      const maxSteps = 80;
      let steps = 0;
      while (
        el.clientWidth > 0 &&
        el.scrollWidth > el.clientWidth &&
        size > minFontSize &&
        steps < maxSteps
      ) {
        size -= 1;
        el.style.fontSize = `${size}px`;
        steps += 1;
      }

      // Keep React state in sync (useful if parent re-renders and wipes inline style).
      setFontSizePx(size > 0 ? size : null);
    };

    const scheduleFit = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(fit);
    };

    scheduleFit();

    // Refit on resize of the element box.
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => scheduleFit());
      ro.observe(el);
    } else {
      // Fallback: refit on window resize.
      window.addEventListener('resize', scheduleFit);
    }

    // Refit when the content changes (Google Translate swaps text nodes).
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(() => scheduleFit());
      mo.observe(el, { childList: true, subtree: true, characterData: true });
    }

    // Refit once fonts are ready (helps avoid clipping with newly loaded Noto fonts).
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      document.fonts.ready.then(() => scheduleFit()).catch(() => {});
    }

    return () => {
      cancelAnimationFrame(rafId);
      try {
        if (ro) ro.disconnect();
      } catch (_) {}
      try {
        if (mo) mo.disconnect();
      } catch (_) {}
      try {
        window.removeEventListener('resize', scheduleFit);
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, minFontSize, maxFontSize]);

  return (
    <span
      ref={elRef}
      className={className}
      title={title}
      style={{
        display: 'block',
        width: '100%',
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'clip',
        // Only set an inline font-size when we had to shrink; otherwise preserve original CSS.
        fontSize: fontSizePx ? `${fontSizePx}px` : undefined,
        ...style,
      }}
      {...rest}
    >
      {children}
    </span>
  );
};

export default AutoFitText;


